"use client"

import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button } from "@/components/ui/button"
import { PrivacyNotice } from "@/components/ui/privacy-notice"
import { useLocalePath } from "@/lib/use-localized-navigation"

interface Props {
  onSubmit: (email: string, marketingOptIn: boolean) => void
  isFocus?: boolean
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function EmailStep({ onSubmit, isFocus = false }: Props) {
  const t = useTranslations("quiz")
  const localePath = useLocalePath()
  const [email, setEmail] = useState("")
  const [optIn, setOptIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = () => {
    if (!EMAIL_RE.test(email)) {
      setError(t("errors.invalidEmail"))
      return
    }
    setError(null)
    onSubmit(email, optIn)
  }

  return (
    <div className="space-y-4">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit()
        }}
        placeholder={t("steps.email.emailPlaceholder")}
        className={
          "focus:border-deep-blue-900 focus:ring-deep-blue-900/20 type-body-lg w-full max-w-md rounded-[12px] border border-neutral-300 bg-white px-4 py-3 focus:ring-2 focus:outline-none " +
          (isFocus ? "mx-auto block" : "")
        }
        autoFocus
      />
      {error && (
        <p
          className={
            "type-body-sm text-red-600" + (isFocus ? " text-center" : "")
          }
        >
          {error}
        </p>
      )}
      <label
        className={
          "type-body-md flex items-start gap-3 text-neutral-700 " +
          (isFocus ? "mx-auto max-w-md" : "")
        }
      >
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className={
            "text-deep-blue-900 mt-1 h-4 w-4 rounded border-neutral-300 " +
            (isFocus ? "ml-0.5" : "")
          }
        />
        <span className={isFocus ? "mt-0.5" : ""}>
          {t("steps.email.marketingOptIn")}
        </span>
      </label>
      <PrivacyNotice
        privacyHref={localePath("/privacy")}
        className={
          isFocus
            ? "type-body-sm text-center text-neutral-400"
            : "type-body-sm text-neutral-400"
        }
      />
      {isFocus ? (
        <div className="flex justify-center pt-3">
          <Button
            onClick={handleSubmit}
            disabled={!email}
            size="lg"
            className="min-w-[280px]"
          >
            {t("steps.email.submit")}
          </Button>
        </div>
      ) : (
        <Button onClick={handleSubmit} disabled={!email}>
          {t("steps.email.submit")}
        </Button>
      )}
    </div>
  )
}
